import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError, authHeaders, withAuthParam } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, Archive, Database, FileSpreadsheet, Trash2, RefreshCw, Loader2 } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${REACT_APP_BACKEND_URL}/api`;

function authedDownload(path) {
  const url = withAuthParam(`${API}${path}`);
  // Use a token query param for the few endpoints that accept it (CV / backup downloads).
  // Other download endpoints require a Bearer header — for those we fetch then blob-download.
  if (path.includes("/backups/") || path.includes("/cv")) {
    window.open(url, "_blank");
    return;
  }
  // Bearer-header download path
  fetch(`${API}${path}`, { headers: authHeaders() })
    .then(async (r) => {
      if (!r.ok) throw new Error((await r.text()).slice(0, 200) || `HTTP ${r.status}`);
      const blob = await r.blob();
      const disp = r.headers.get("content-disposition") || "";
      const m = /filename="?([^";]+)"?/.exec(disp);
      const filename = m ? m[1] : path.split("/").pop();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
    })
    .catch((e) => alert("Download failed: " + e.message));
}

function ExportRow({ label, basePath, testidBase }) {
  return (
    <div className="flex items-center justify-between border border-border bg-white px-4 py-3" data-testid={`${testidBase}-row`}>
      <div>
        <div className="font-medium text-sm" data-testid={`${testidBase}-label`}>{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">Filtered by no parameters — full dataset</div>
      </div>
      <div className="flex items-center gap-2">
        {["csv", "xlsx", "json"].map((fmt) => (
          <button
            key={fmt}
            onClick={() => authedDownload(`${basePath}?format=${fmt}`)}
            className="text-xs uppercase tracking-wider border border-border px-3 py-1.5 hover:bg-execution hover:text-white hover:border-execution transition-colors"
            data-testid={`${testidBase}-${fmt}`}
          >
            {fmt}
          </button>
        ))}
      </div>
    </div>
  );
}

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / 1024 / 1024).toFixed(2) + " MB";
}

export default function AdminDataManagement() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [includeCvs, setIncludeCvs] = useState(false);
  const [includeBackupCvs, setIncludeBackupCvs] = useState(false);
  const [error, setError] = useState("");
  const [rateBuckets, setRateBuckets] = useState(null);
  const [byOrg, setByOrg] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, b, r1, r2] = await Promise.all([
        api.get("/data-summary"),
        api.get("/backups"),
        api.get("/reports/candidates/rate-buckets"),
        api.get("/reports/enquiries/by-organisation"),
      ]);
      setSummary(s.data);
      setBackups(b.data);
      setRateBuckets(r1.data);
      setByOrg(r2.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const createBackup = async () => {
    setCreating(true);
    setError("");
    try {
      await api.post(`/backups${includeBackupCvs ? "?include_cvs=true" : ""}`);
      await reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const deleteBackup = async (id) => {
    if (!window.confirm("Delete this backup? The archive will be removed from object storage too.")) return;
    try {
      await api.delete(`/backups/${id}`);
      await reload();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  if (user?.role !== "admin") {
    return (
      <AdminLayout title="Data Management" subtitle="Admin only">
        <div className="text-sm text-muted-foreground">Admin privileges required.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Data Management"
      subtitle="Export, migrate and back up the Nexra database"
      actions={
        <button onClick={reload} className="btn-secondary inline-flex items-center gap-2" data-testid="dm-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      }
    >
      {error && <div className="mb-4 text-sm text-destructive border border-destructive/30 px-3 py-2 bg-destructive/5">{error}</div>}

      {/* Data ownership notice */}
      <div className="mb-8 border border-border bg-muted px-5 py-4" data-testid="dm-ownership-notice">
        <div className="flex items-start gap-3">
          <Database className="h-5 w-5 text-execution mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Nexra owns 100% of this data.</p>
            <p className="text-muted-foreground mt-1 leading-relaxed">
              Every export below produces a standard, open-format archive (CSV / XLSX / JSON / ZIP) that can be imported
              into any CRM, ATS, ERP or custom platform. No proprietary tooling required.
            </p>
          </div>
        </div>
      </div>

      {/* Domain counts */}
      <section className="mb-10" data-testid="dm-summary">
        <h2 className="overline mb-3">Domain snapshot</h2>
        {loading || !summary ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {Object.entries(summary.counts).map(([k, v]) => (
              <div key={k} className="border border-border bg-white px-4 py-3" data-testid={`dm-count-${k}`}>
                <div className="text-2xl font-heading font-semibold">{v}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Per-domain exports */}
      <section className="mb-10">
        <h2 className="overline mb-3">Per-domain exports</h2>
        <div className="space-y-2">
          <ExportRow label="Candidates" basePath="/candidates/export" testidBase="dm-export-candidates" />
          <ExportRow label="Client enquiries" basePath="/enquiries/export" testidBase="dm-export-enquiries" />
          <ExportRow label="Contact messages" basePath="/contacts/export" testidBase="dm-export-contacts" />
        </div>
      </section>

      {/* Bundle exports */}
      <section className="mb-10">
        <h2 className="overline mb-3">Bundle exports</h2>
        <div className="space-y-2">
          <div className="border border-border bg-white px-4 py-4" data-testid="dm-cv-bundle">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium text-sm inline-flex items-center gap-2">
                  <Archive className="h-4 w-4" /> Candidate document bundle
                </div>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  ZIP containing <code>Candidates/candidates.csv</code> + every CV file in
                  <code> Candidates/candidate-cvs/</code>. Designed for direct migration into a new ATS.
                </p>
              </div>
              <button
                onClick={() => authedDownload(`/export/cv-bundle`)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="dm-cv-bundle-download"
              >
                <Download className="h-4 w-4" /> Download ZIP
              </button>
            </div>
          </div>

          <div className="border border-border bg-white px-4 py-4" data-testid="dm-full-export">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-medium text-sm inline-flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" /> Full business export
                </div>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  Single ZIP with candidates, enquiries, contacts, CMS content, email templates &amp; logs,
                  users (passwords redacted) and system metadata. Includes <code>MANIFEST.json</code>.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs inline-flex items-center gap-1.5 select-none">
                  <input
                    type="checkbox"
                    checked={includeCvs}
                    onChange={(e) => setIncludeCvs(e.target.checked)}
                    className="h-3.5 w-3.5 accent-execution"
                    data-testid="dm-full-export-include-cvs"
                  />
                  include CV files
                </label>
                <button
                  onClick={() => authedDownload(`/export/full${includeCvs ? "?include_cvs=true" : ""}`)}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="dm-full-export-download"
                >
                  <Download className="h-4 w-4" /> Download ZIP
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Backups */}
      <section className="mb-10" data-testid="dm-backups">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <h2 className="overline">Backups</h2>
          <div className="flex items-center gap-3">
            <label className="text-xs inline-flex items-center gap-1.5 select-none">
              <input
                type="checkbox"
                checked={includeBackupCvs}
                onChange={(e) => setIncludeBackupCvs(e.target.checked)}
                className="h-3.5 w-3.5 accent-execution"
                data-testid="dm-backup-include-cvs"
              />
              include CV files
            </label>
            <button
              onClick={createBackup}
              disabled={creating}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="dm-create-backup"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
              {creating ? "Creating…" : "Create backup"}
            </button>
          </div>
        </div>
        {backups.length === 0 ? (
          <div className="border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="dm-backups-empty">
            No backups yet. Click "Create backup" to snapshot all data now.
          </div>
        ) : (
          <div className="border border-border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Created</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Size</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Includes CVs</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="text-right px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.id} className="border-t border-border" data-testid={`dm-backup-row-${b.id}`}>
                    <td className="px-4 py-3">
                      <div>{new Date(b.started_at).toLocaleString()}</div>
                      <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{b.id}</div>
                    </td>
                    <td className="px-4 py-3">{formatBytes(b.size_bytes)}</td>
                    <td className="px-4 py-3 text-xs">{b.includes_cv_files ? "Yes" : "No"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${b.status === "success" ? "bg-execution text-white" : "bg-destructive text-white"}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {b.status === "success" && (
                        <button
                          onClick={() => authedDownload(`/backups/${b.id}/download`)}
                          className="text-xs inline-flex items-center gap-1 border border-border px-2 py-1 hover:bg-execution hover:text-white hover:border-execution transition-colors mr-2"
                          data-testid={`dm-backup-download-${b.id}`}
                        >
                          <Download className="h-3 w-3" /> Download
                        </button>
                      )}
                      <button
                        onClick={() => deleteBackup(b.id)}
                        className="h-7 w-7 inline-flex items-center justify-center border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
                        data-testid={`dm-backup-delete-${b.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Reports */}
      <section className="mb-4" data-testid="dm-reports">
        <h2 className="overline mb-3">Reports</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="border border-border bg-white p-4" data-testid="dm-report-rate-buckets">
            <h3 className="font-medium text-sm mb-3">Candidates by rate range</h3>
            {rateBuckets ? (
              <ul className="space-y-1.5 text-sm">
                {Object.entries(rateBuckets.buckets).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between border-b border-border last:border-0 py-1">
                    <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span>
                    <span className="font-mono">{v}</span>
                  </li>
                ))}
              </ul>
            ) : <div className="text-xs text-muted-foreground">Loading…</div>}
          </div>
          <div className="border border-border bg-white p-4" data-testid="dm-report-by-org">
            <h3 className="font-medium text-sm mb-3">Enquiries by organisation</h3>
            {byOrg ? (
              <ul className="space-y-1.5 text-sm max-h-[260px] overflow-y-auto">
                {byOrg.organisations.slice(0, 30).map((o) => (
                  <li key={o.organisation} className="flex items-center justify-between border-b border-border last:border-0 py-1">
                    <span className="text-muted-foreground truncate" title={o.organisation}>{o.organisation}</span>
                    <span className="font-mono">{o.count}</span>
                  </li>
                ))}
              </ul>
            ) : <div className="text-xs text-muted-foreground">Loading…</div>}
          </div>
        </div>
      </section>
    </AdminLayout>
  );
}
